import { InjectionToken } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { InitializableInterface } from './initializable.interface';

/**
 * Public contract of the date-manipulation-check service: an anti-tampering control that detects
 * a manipulated system clock and blocks the application when it finds one. It tracks two
 * timestamps — the highest local system time seen so far, and the last timestamp received from
 * the trusted gateway — and rejects any candidate that falls behind either by more than a small
 * tolerance.
 *
 * Implemented by both the legacy `DateManipulationCheckService` (encrypted-`localStorage`-backed)
 * and the NgRx-based `DateManipulationCheckNgrxService`; consumers depend on this interface via
 * {@link DateManipulationCheckInstanceToken} rather than on a concrete class.
 */
export interface DateManipulationCheckInterface extends InitializableInterface {
    /**
     * Fetches the external (gateway) timestamp, restores the persisted timestamps, and runs the
     * initial validity check. Typed `Observable<unknown>` rather than `Observable<void>` because
     * the legacy implementation returns a nested observable and this interface has to keep
     * conforming to it while it is still the default (see `date-manipulation-check-ngrx-migration.md`,
     * Decision 5 — the init check's invalid-date error is swallowed, not surfaced, in both
     * implementations).
     */
    init(): Observable<unknown>;

    /**
     * Checks whether `timestamp` is valid, i.e. at or after both the last valid internal and
     * external timestamps (within tolerance). Advances the internal timestamp on success and
     * shows a blocking modal on failure.
     *
     * @param timestamp the timestamp to validate, typically the current system time
     * @returns whether the timestamp was accepted as valid
     */
    checkTimestamp(timestamp: moment.Moment): Promise<boolean>;

    /**
     * Records a timestamp received from a trusted source (the gateway) as the new external
     * baseline.
     *
     * @param timestamp the trusted timestamp
     */
    setExternalTimestamp(timestamp: moment.Moment): Observable<void>;
}

/**
 * DI token for {@link DateManipulationCheckInterface}. Lets the app choose which implementation
 * to provide (legacy `DateManipulationCheckService` or the NgRx-based
 * `DateManipulationCheckNgrxService`) without consumers depending on a concrete class.
 */
export const DateManipulationCheckInstanceToken = new InjectionToken<DateManipulationCheckInterface>(
    'DateManipulationCheckInterface',
);
